import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  getMessaging,
  isSupported,
  onMessage,
  onRegistered,
  onUnregistered,
  register as registerMessaging,
  unregister as unregisterMessaging,
} from 'firebase/messaging';

import {
  disablePushInstallation,
  getNotificationDisplay,
  getNotificationPreferences,
  registerPushInstallation,
  updateNotificationPreference,
} from './api';


const INSTALLATION_STORAGE_KEY = 'dopatize:firebase-installation-id';
export const PUSH_PREFERENCE_TYPES = [
  'due_date_reminder',
  'friend_request',
  'shared_task',
  'task_timer',
  'event_updates',
  'event_preparation',
];
let registrationPromise = null;

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const firebasePushConfigured = () => Boolean(
  firebaseConfig.apiKey
  && firebaseConfig.projectId
  && firebaseConfig.messagingSenderId
  && firebaseConfig.appId
  && process.env.REACT_APP_FIREBASE_VAPID_KEY
);

export const pushPermissionState = () => (
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
);

export const currentPushDeviceRegistered = () => Boolean(
  typeof window !== 'undefined'
  && window.localStorage.getItem(INSTALLATION_STORAGE_KEY)
);

export const firebasePushSupported = async () => (
  firebasePushConfigured()
  && typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && await isSupported()
);

const messagingContext = async () => {
  if (!await firebasePushSupported()) {
    throw new Error('Push notifications are not supported or configured on this device.');
  }
  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  const serviceWorkerRegistration = await navigator.serviceWorker.register(
    '/firebase-messaging-sw.js',
  );
  return {
    messaging: getMessaging(app),
    serviceWorkerRegistration,
  };
};

const platformName = () => (
  navigator.userAgentData?.platform || navigator.platform || 'web'
);

const waitForInstallation = (messaging) => {
  let unsubscribeRegistered = () => {};
  let timeoutId;
  let settled = false;
  const stop = () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeoutId);
    unsubscribeRegistered();
  };
  const promise = new Promise((resolve, reject) => {
    timeoutId = window.setTimeout(() => {
      stop();
      reject(new Error('Firebase registration timed out.'));
    }, 15000);

    const unsubscribe = onRegistered(messaging, async (installationId) => {
      try {
        await registerPushInstallation(installationId, platformName());
        window.localStorage.setItem(INSTALLATION_STORAGE_KEY, installationId);
        stop();
        resolve(installationId);
      } catch (error) {
        stop();
        reject(error);
      }
    });
    unsubscribeRegistered = unsubscribe;
    if (settled) unsubscribeRegistered();
  });
  return { promise, stop };
};

const performPushRegistration = async () => {
  if (pushPermissionState() !== 'granted') {
    throw new Error('Notification permission has not been granted.');
  }
  const { messaging, serviceWorkerRegistration } = await messagingContext();
  const installation = waitForInstallation(messaging);
  try {
    await registerMessaging(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration,
    });
    return await installation.promise;
  } catch (error) {
    installation.stop();
    throw error;
  }
};

export const registerCurrentPushDevice = async () => {
  if (!registrationPromise) {
    registrationPromise = performPushRegistration().finally(() => {
      registrationPromise = null;
    });
  }
  return registrationPromise;
};

const anyPushPreferenceEnabled = (settings) => PUSH_PREFERENCE_TYPES.some(
  (notificationType) => settings.preferences?.[notificationType]?.enabled,
);

export const enablePushPreference = async (notificationType) => {
  if (!PUSH_PREFERENCE_TYPES.includes(notificationType)) {
    throw new Error('Unsupported notification preference.');
  }
  if (!await firebasePushSupported()) {
    throw new Error('Push notifications are not supported or configured on this device.');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }
  await registerCurrentPushDevice();
  return updateNotificationPreference(notificationType, true);
};

export const enableDueDatePush = async () => enablePushPreference('due_date_reminder');

export const unregisterCurrentPushDevice = async () => {
  const installationId = window.localStorage.getItem(INSTALLATION_STORAGE_KEY);
  // Explicit cleanup owns this ID; Firebase's callback must not disable it twice.
  window.localStorage.removeItem(INSTALLATION_STORAGE_KEY);
  if (installationId) {
    try {
      await disablePushInstallation(installationId);
    } catch (error) {
      console.error('Unable to disable the push registration:', error);
    }
  }
  if (await firebasePushSupported()) {
    try {
      const { messaging } = await messagingContext();
      await unregisterMessaging(messaging);
    } catch (error) {
      console.error('Unable to unregister this device from Firebase:', error);
    }
  }
};

export const disablePushPreference = async (notificationType) => {
  if (!PUSH_PREFERENCE_TYPES.includes(notificationType)) {
    throw new Error('Unsupported notification preference.');
  }
  const settings = await updateNotificationPreference(notificationType, false);
  if (anyPushPreferenceEnabled(settings)) return settings;
  await unregisterCurrentPushDevice();
  return getNotificationPreferences();
};

export const disableDueDatePush = async () => disablePushPreference('due_date_reminder');

export const syncPushRegistration = async () => {
  if (
    pushPermissionState() !== 'granted'
    || !await firebasePushSupported()
  ) {
    return false;
  }
  const settings = await getNotificationPreferences();
  if (!anyPushPreferenceEnabled(settings)) {
    return false;
  }
  await registerCurrentPushDevice();
  return true;
};

export const syncDueDatePushRegistration = syncPushRegistration;

export const subscribeToForegroundPush = (handler) => {
  let cancelled = false;
  let unsubscribeMessage;
  let unsubscribeUnregistered;

  messagingContext()
    .then(({ messaging }) => {
      if (cancelled) return;
      unsubscribeMessage = onMessage(messaging, async (message) => {
        const data = message?.data || {};
        const notificationId = data.notification_id;
        if (!notificationId) return;
        if (data.title) {
          handler({
            notification_id: notificationId,
            notification_type: data.notification_type,
            selected_task_id: data.selected_task_id,
            title: data.title,
            body: data.body,
            url: data.url,
            tag: data.tag,
          });
          return;
        }
        try {
          const payload = await getNotificationDisplay(notificationId);
          if (payload?.title) handler(payload);
        } catch (error) {
          console.error('Unable to display foreground push notification:', error);
        }
      });
      unsubscribeUnregistered = onUnregistered(messaging, async (installationId) => {
        if (window.localStorage.getItem(INSTALLATION_STORAGE_KEY) !== installationId) return;
        window.localStorage.removeItem(INSTALLATION_STORAGE_KEY);
        try {
          await disablePushInstallation(installationId);
        } catch (error) {
          console.error('Unable to remove an unregistered Firebase installation:', error);
        }
      });
    })
    .catch(() => {
      // Unsupported and unconfigured environments continue without browser push.
    });

  return () => {
    cancelled = true;
    if (unsubscribeMessage) unsubscribeMessage();
    if (unsubscribeUnregistered) unsubscribeUnregistered();
  };
};
